import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { COGNITO_ENABLED } from "../config";
import { AuthLayout } from "../components/AuthLayout";

// Provider password reset. With Cognito configured this is the real flow: resetPassword emails a
// code, confirmResetPassword sets the new password. On dev-sbx1 (no Cognito) it stays simulated and
// lands on the check-email confirmation.
function CognitoResetPassword() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState<"request" | "confirm" | "done">("request");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitRequest(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await resetPassword({ username: email.trim() });
      if (nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") setStep("confirm");
      else setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn’t start the reset. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitConfirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: code.trim(),
        newPassword,
      });
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn’t work — check the code and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (step === "done") {
    return (
      <AuthLayout>
        <div className="auth-form">
          <h2>
            Password <em>updated.</em>
          </h2>
          <p>Your password has been reset. Sign in with your new password.</p>
          <div className="auth-meta">
            <Link to="/sign-in">&larr; Back to sign in</Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (step === "confirm") {
    return (
      <AuthLayout>
        <form className="auth-form" onSubmit={submitConfirm}>
          <h2>
            Check your <em>email.</em>
          </h2>
          <p>
            We sent a code to <strong>{email}</strong>. Enter it below with your new password.
          </p>
          <div className="input-group">
            <label htmlFor="rc">Reset code</label>
            <input
              id="rc"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="rp">New password</label>
            <input
              id="rp"
              type="password"
              autoComplete="new-password"
              placeholder="12+ characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={12}
            />
          </div>
          {error && (
            <div className="auth-meta" role="alert" style={{ color: "var(--red)" }}>{error}</div>
          )}
          <button type="submit" className="btn btn-primary btn-lg" disabled={busy || code.length < 6}>
            {busy ? "Resetting…" : "Reset password"}
          </button>
          <div className="auth-meta">
            <Link to="/sign-in">&larr; Back to sign in</Link>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form className="auth-form" onSubmit={submitRequest}>
        <h2>
          Reset your <em>password.</em>
        </h2>
        <p>Enter your work email and we&rsquo;ll send a reset code.</p>
        <div className="input-group">
          <label htmlFor="re">Email address</label>
          <input
            id="re"
            type="email"
            autoComplete="email"
            placeholder="dr.you@championcare.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {error && (
          <div className="auth-meta" role="alert" style={{ color: "var(--red)" }}>{error}</div>
        )}
        <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? "Sending…" : "Send reset code"}
        </button>
        <div className="auth-meta">
          <Link to="/sign-in">&larr; Back to sign in</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (COGNITO_ENABLED) return <CognitoResetPassword />;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    navigate("/check-email");
  }

  return (
    <AuthLayout>
      <form className="auth-form" onSubmit={submit}>
        <h2>
          Reset your <em>password.</em>
        </h2>
        <p>Enter your work email and we&rsquo;ll send a reset link.</p>
        <div className="input-group">
          <label htmlFor="re">Email address</label>
          <input id="re" type="email" placeholder="dr.you@championcare.com" required />
        </div>
        <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? "Sending…" : "Send reset link"}
        </button>
        <div className="auth-meta">
          <Link to="/sign-in">&larr; Back to sign in</Link>
        </div>
        <div className="auth-meta">Dev environment — no email is actually sent.</div>
      </form>
    </AuthLayout>
  );
}
